import React, { useState, useRef, useEffect, Children } from 'react';
import PropTypes from 'prop-types';
import { PressedBehavior, FocusBehavior } from '@weave-design/behaviors';
import { cx, css } from 'emotion';
import { ThemeContext } from '@weave-design/theme-context';
import { CheckmarkSUI, CheckmarkXsUI } from '@weave-design/icons';
import { createCustomClassNames } from '@weave-design/utils';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);

  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }

  return keys;
}

function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }

  return target;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function selectOption(optionId, activeOptionsArray, multiple) {
  const selectedOptions = activeOptionsArray;

  if (selectedOptions.indexOf(optionId) === -1) {
    if (!multiple) {
      selectedOptions.length = 0;
    }

    selectedOptions.unshift(optionId);
  } else if (selectedOptions.indexOf(optionId) > -1) {
    if (multiple) {
      selectedOptions.splice(selectedOptions.indexOf(optionId), 1);
    } else {
      selectedOptions.length = 0;
    }
  }

  return selectedOptions;
}

function scrollInViewport(option) {
  const optionBounding = option.getBoundingClientRect();

  if (optionBounding.top < 0 || optionBounding.bottom > (window.innerHeight || document.documentElement.clientHeight)) {
    // align to the top if the top of Option is out of the viewport
    if (optionBounding.top < 0) {
      option.scrollIntoView(true);
    } // align to the bottom if the bottom of Option is out of the viewport


    if (optionBounding.bottom > (window.innerHeight || document.documentElement.clientHeight)) {
      option.scrollIntoView(false);
    }
  }
}

function scrollInListbox(option, menu) {
  if (option && menu.scrollHeight > menu.clientHeight) {
    const scrollBottom = menu.clientHeight + menu.scrollTop;
    const elementBottom = option.offsetTop + option.offsetHeight;

    if (elementBottom > scrollBottom) {
      // eslint-disable-next-line no-param-reassign
      menu.scrollTop = elementBottom - menu.clientHeight;
    }

    if (option.offsetTop < menu.scrollTop) {
      // eslint-disable-next-line no-param-reassign
      menu.scrollTop = option.offsetTop;
    }
  }
}

function checkScroll(optionId, menu) {
  const option = document.getElementById(optionId); // scroll if out of viewport

  scrollInViewport(option); // scroll within the menu

  scrollInListbox(option, menu);
}

const MenuBehavior = props => {
  const isControlled = () => props.selected !== undefined;

  const [activeOptionHook, setActiveOptionHook] = useState(isControlled() ? props.selected : props.defaultSelected);
  const [highlightIndexHook, setHighlightIndexHook] = useState(0);
  const [optionInfoHook, setOptionInfoHook] = useState(null);
  const [previousEventHook, setPreviousEventHook] = useState(null);
  const menuRef = useRef(null);

  const setMenuRef = element => {
    if (props.menuRef) {
      props.menuRef(element);
    }

    menuRef.current = element;
  };

  const setOptionsInfo = optionInfo => {
    setOptionInfoHook(optionInfo);
  };

  const getOptionsInfo = () => optionInfoHook;

  const setActiveOption = activeOption => {
    const {
      onChange,
      unselect
    } = props;

    if (onChange) {
      if (unselect) {
        props.onChange(activeOption);
        setActiveOptionHook(activeOption);
      } else {
        let newActiveOption;

        if (activeOption.length === 0 || activeOption.length < activeOptionHook.length) {
          newActiveOption = activeOptionHook;
          props.onChange(activeOptionHook);
        } else {
          newActiveOption = activeOption;
          props.onChange(activeOption);
        }

        setActiveOptionHook(newActiveOption);
      }
    }
  };

  const getActiveOption = () => activeOptionHook;

  const setHighlightIndex = highlightIndex => {
    setHighlightIndexHook(highlightIndex);
  };

  const getHighlightIndex = () => highlightIndexHook;

  const getPreviousEvent = () => previousEventHook;

  const setPreviousEvent = previousEvent => {
    setPreviousEventHook(previousEvent);
  }; // eslint-disable-next-line no-unused-vars

  const handleFocus = event => {
    if (props.onFocus) {
      props.onFocus(event);
    }

    event.stopPropagation();
  };

  const handleBlur = event => {
    if (props.onBlur) {
      props.onBlur(event);
    }

    event.stopPropagation();
    setHighlightIndex(0);
  };

  const handleKeyDown = event => {
    const {
      multiple,
      onKeyDown
    } = props;
    const options = optionInfoHook;
    const highlightableIndexes = [];
    const {
      id
    } = (optionInfoHook === null || optionInfoHook === void 0 ? void 0 : optionInfoHook[getHighlightIndex() - 1]) || menuRef.current.children[0].id;

    const callKeyDown = currentHighlightedId => {
      if (onKeyDown) {
        onKeyDown(event, {
          currentHighlightedId
        });
      }
    };

    Object.keys(options).forEach(index => {
      if (!options[index].disabled && options[index].role !== `presentation`) {
        highlightableIndexes.push(Number(index) + 1);
      }
    });
    setPreviousEvent(event.type);

    switch (event.code) {
      case "ArrowDown":
        {
          const currentIndex = highlightableIndexes.indexOf(getHighlightIndex());
          const lastIndex = highlightableIndexes.length - 1;

          if (currentIndex === lastIndex) {
            setHighlightIndex(highlightableIndexes[0]);
            callKeyDown(getOptionsInfo()[highlightableIndexes[0] - 1].id);
            checkScroll(getOptionsInfo()[highlightableIndexes[0] - 1].id, menuRef.current);
          } else {
            setHighlightIndex(highlightableIndexes[currentIndex + 1]);
            callKeyDown(getOptionsInfo()[highlightableIndexes[currentIndex + 1] - 1].id);
            checkScroll(getOptionsInfo()[highlightableIndexes[currentIndex + 1] - 1].id, menuRef.current);
          }

          event.preventDefault();
          break;
        }

      case "ArrowUp":
        {
          const currentIndex = highlightableIndexes.indexOf(getHighlightIndex());
          const lastIndex = highlightableIndexes.length - 1;

          if (currentIndex <= 0) {
            setHighlightIndex(highlightableIndexes[lastIndex]);
            callKeyDown(getOptionsInfo()[highlightableIndexes[lastIndex] - 1].id);
            checkScroll(getOptionsInfo()[highlightableIndexes[lastIndex] - 1].id, menuRef.current);
          } else {
            setHighlightIndex(highlightableIndexes[currentIndex - 1]);
            callKeyDown(getOptionsInfo()[highlightableIndexes[currentIndex - 1] - 1].id);
            checkScroll(getOptionsInfo()[highlightableIndexes[currentIndex - 1] - 1].id, menuRef.current);
          }

          event.preventDefault();
          break;
        }

      case "Enter":
      case "Space":
        {
          const activeOptionsArray = [...activeOptionHook];
          const activeOptions = selectOption(id, activeOptionsArray, multiple);
          callKeyDown(id);

          if (isControlled()) {
            return;
          }

          setActiveOption(activeOptions);
          event.preventDefault();
          break;
        }

      default:
        callKeyDown(id);
    }
  };

  const handleMouseMove = event => {
    // don't let this bubble up from Menu to MenuGroup
    event.stopPropagation();

    if (getPreviousEvent() === event.type) {
      return;
    }

    setPreviousEvent(event.type);
  };

  const getActiveOptionMethod = props.getActiveOption ? props.getActiveOption : getActiveOption;
  const getHighlightIndexMethod = props.getHighlightIndex ? props.getHighlightIndex : getHighlightIndex;
  const getOptionsInfoMethod = props.getOptionsInfo ? props.getOptionsInfo : getOptionsInfo;
  const getPreviousEventMethod = props.getPreviousEvent ? props.getPreviousEvent : getPreviousEvent;
  const handleBlurMethod = props.handleBlur ? props.handleBlur : handleBlur;
  const handleFocusMethod = props.handleFocus ? props.handleFocus : handleFocus;
  const handleKeyDownMethod = props.handleKeyDown ? props.handleKeyDown : handleKeyDown;
  const handleMouseMoveMethod = props.handleMouseMove ? props.handleMouseMove : handleMouseMove;
  const setActiveOptionMethod = props.setActiveOption ? props.setActiveOption : setActiveOption;
  const setHighlightIndexMethod = props.setHighlightIndex ? props.setHighlightIndex : setHighlightIndex;
  const setOptionsInfoMethod = props.setOptionsInfo ? props.setOptionsInfo : setOptionsInfo;
  const setPreviousEventMethod = props.setPreviousEvent ? props.setPreviousEvent : setPreviousEvent;
  useEffect(() => {
    if (props.selected) {
      setActiveOptionHook(props.selected);

      if (props.onChange) {
        props.onChange(props.selected);
      }
    }
  }, [props.selected]);
  useEffect(() => {
    if (props.highlighted >= 0) {
      setHighlightIndexHook(props.highlighted + 1);
    }
  }, [props.highlighted]);
  return props.children({
    getActiveOption: getActiveOptionMethod,
    getHighlightIndex: getHighlightIndexMethod,
    getOptionsInfo: getOptionsInfoMethod,
    getPreviousEvent: getPreviousEventMethod,
    handleBlur: handleBlurMethod,
    handleFocus: handleFocusMethod,
    handleKeyDown: handleKeyDownMethod,
    handleMouseMove: handleMouseMoveMethod,
    setActiveOption: setActiveOptionMethod,
    setHighlightIndex: setHighlightIndexMethod,
    setMenuRef,
    setOptionsInfo: setOptionsInfoMethod,
    setPreviousEvent: setPreviousEventMethod
  });
};

MenuBehavior.displayName = "MenuBehavior";
MenuBehavior.propTypes = {
  children: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  defaultSelected: PropTypes.arrayOf(PropTypes.any),
  getActiveOption: PropTypes.func,
  getHighlightIndex: PropTypes.func,
  getOptionsInfo: PropTypes.func,
  getPreviousEvent: PropTypes.func,
  handleBlur: PropTypes.func,
  handleFocus: PropTypes.func,
  handleKeyDown: PropTypes.func,
  handleMouseMove: PropTypes.func,
  highlighted: PropTypes.number,
  menuRef: PropTypes.func,
  multiple: PropTypes.bool,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  selected: PropTypes.arrayOf(PropTypes.any),
  setActiveOption: PropTypes.func,
  setHighlightIndex: PropTypes.func,
  setOptionsInfo: PropTypes.func,
  setPreviousEvent: PropTypes.func,
  unselect: PropTypes.bool
};
MenuBehavior.defaultProps = {
  defaultSelected: []
};

const OptionBehavior = props => {
  const getIndexFromId = id => {
    const options = props.getOptionsInfo();
    const optionIds = [];

    if (options !== null) {
      Object.keys(options).forEach(index => {
        optionIds.push(options[index].id);
      });
    }

    return optionIds.indexOf(id) > -1 ? optionIds.indexOf(id) : null;
  };

  const handleClick = event => {
    const {
      disabled,
      getActiveOption,
      id,
      multiple,
      onClick,
      role,
      selected,
      setActiveOption
    } = props;
    const activeOptionsArray = [...getActiveOption()];
    const activeOptions = selectOption(id, activeOptionsArray, multiple);

    if (onClick) {
      onClick(event);
    }

    if (disabled || role === `presentation`) {
      return;
    } // do nothing if controlled


    if (selected !== undefined) {
      return;
    }

    setActiveOption(activeOptions);
  };

  const handleMouseEnter = event => {
    if (props.onMouseEnter) {
      props.onMouseEnter(event);
    } // we don't want to set the highlight state on hover when scrolling via keyboard


    if (props.getPreviousEvent() === `keydown`) {
      return;
    }

    if (props.disabled || props.role === `presentation`) {
      return;
    }

    if (props.setHighlightIndex) {
      props.setHighlightIndex(Number(getIndexFromId(props.id)) + 1);
    }
  };

  const handleMouseLeave = event => {
    if (props.onMouseLeave) {
      props.onMouseLeave(event);
    } // we don't want to set the highlight state on hover when scrolling via keyboard


    if (props.getPreviousEvent() === `keydown`) {
      return;
    }

    if (props.setHighlightIndex) {
      props.setHighlightIndex(0);
    }
  };

  const isActive = () => {
    var _getActiveOption;

    const {
      id,
      getActiveOption,
      multiple
    } = props;

    if (multiple) {
      return getActiveOption().indexOf(id) > -1;
    }

    return id === ((_getActiveOption = getActiveOption()) === null || _getActiveOption === void 0 ? void 0 : _getActiveOption[0]);
  };

  return props.children({
    getIndexFromId,
    handleClick,
    handleMouseEnter,
    handleMouseLeave,
    isActive
  });
};

OptionBehavior.displayName = "OptionBehavior";
OptionBehavior.propTypes = {
  children: PropTypes.func,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  getActiveOption: PropTypes.func,
  getOptionsInfo: PropTypes.func,
  getPreviousEvent: PropTypes.func,
  multiple: PropTypes.bool,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  role: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  selected: PropTypes.arrayOf(PropTypes.any),
  setActiveOption: PropTypes.func,
  setHighlightIndex: PropTypes.func
};

function getRulesByPresentation(themeData) {
  return {
    fontSize: themeData[`menu.header.fontSize`],
    fontWeight: themeData[`menu.header.fontWeight`],
    color: themeData[`menu.header.fontColor`],
    opacity: 0.5,
    textTransform: `uppercase`,
    cursor: `default`,
    padding: `0 
        ${themeData["menu.item.paddingHorizontal"]}
        ${themeData["menu.header.marginBottom"]}`
  };
}

function stylesheet(props, themeData) {
  const {
    disabled,
    divider,
    highlighted,
    isPressed,
    role,
    selected,
    unselect,
    stylesheet: customStylesheet
  } = props;
  const styles = {
    menuGroup: {
      overflowY: `auto`,
      outline: 0
    },
    menu: {
      borderBottom: divider ? `1px solid ${themeData[`menu.divider.backgroundColor`]}` : {},
      boxSizing: `border-box`,
      cursor: `pointer`,
      fontFamily: themeData[`menu.fontFamily`],
      listStyle: `none`,
      margin: 0,
      outline: 0,
      padding: `${themeData["menu.container.paddingVertical"]} 0`,
      overflowY: `auto`
    },
    menuOption: _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({
      display: `flex`,
      color: themeData[`menu.label.fontColor`],
      fontSize: themeData[`menu.label.fontSize`],
      alignItems: `center`,
      minHeight: themeData[`menu.item.minHeight`],
      padding: `${themeData["menu.item.paddingVertical"]}
        ${themeData["menu.item.paddingHorizontal"]}`,
      transition: `background-color 0.3s cubic-bezier(0.4,0,0.2,1)`
    }, selected && !disabled ? {
      fontWeight: themeData[`menu.label.selected.fontWeight`]
    } : {
      fontWeight: themeData[`menu.label.default.fontWeight`]
    }), highlighted && !disabled ? {
      backgroundColor: themeData[`menu.item.hover.backgroundColor`],
      transition: `background-color 0.3s cubic-bezier(0.4,0,0.2,1)`
    } : {}), isPressed && !disabled && role !== `presentation` ? {
      backgroundColor: themeData[`menu.item.pressed.backgroundColor`],
      transition: `background-color 0.3s cubic-bezier(0.4,0,0.2,1)`
    } : {}), isPressed && selected && !unselect ? {
      backgroundColor: themeData[`menu.item.hover.backgroundColor`]
    } : {}), disabled ? {
      opacity: themeData[`colorScheme.opacity.disabled`],
      pointerEvents: `none`
    } : {}), role === `presentation` ? getRulesByPresentation(themeData) : {}),
    checkmarkWrapper: {
      display: `flex`,
      alignItems: `center`,
      position: `relative`,
      height: themeData[`menu.item.minHeight`],
      justifyContent: `center`,
      marginRight: themeData[`menu.item.paddingHorizontal`],
      "& > svg > *": _objectSpread2(_objectSpread2({
        opacity: 0,
        transition: `opacity 0.3s cubic-bezier(0.4,0,0.2,1)`
      }, highlighted && !disabled ? {
        fill: themeData[`menu.item.checkmark.hover.iconColor`],
        opacity: 1,
        transition: `opacity 0.3s cubic-bezier(0.4,0,0.2,1)`
      } : {}), selected && !disabled ? {
        fill: themeData[`menu.item.checkmark.active.iconColor`],
        opacity: 1,
        transition: `opacity 0.3s cubic-bezier(0.4,0,0.2,1)`
      } : {})
    },
    assetWrapper: {
      display: `flex`,
      justifyContent: `center`,
      marginRight: themeData[`menu.item.paddingHorizontal`],
      fontWeight: 400
    },
    optionContentWrapper: {
      display: `flex`,
      width: `100%`
    },
    shortcutWrapper: {
      color: themeData[`menu.shortcut.fontColor`],
      fontSize: themeData[`menu.shortcut.fontSize`],
      alignSelf: `center`,
      fontWeight: themeData[`menu.shortcut.fontWeight`],
      marginLeft: `auto`
    }
  };
  return customStylesheet ? customStylesheet(styles, props, themeData) : styles;
}

const roles = Object.freeze({
  OPTION: "option",
  PRESENTATION: "presentation"
});
const AVAILABLE_ROLES = Object.freeze(Object.values(roles));

const _excluded$5 = ["asset", "checkmark", "children", "optionRef", "disabled", "highlighted", "id", "isPressed", "role", "selected", "shortcut", "stylesheet", "unselect"];

const OptionPresenter = props => {
  const {
    asset,
    checkmark,
    children,
    optionRef,
    disabled,
    highlighted,
    id,
    isPressed,
    role,
    selected,
    shortcut,
    stylesheet: customStylesheet,
    unselect
  } = props,
        otherProps = _objectWithoutProperties(props, _excluded$5);

  const {
    className
  } = otherProps;
  const menuOptionClassName = createCustomClassNames(className, `menu-option`);
  const checkmarkWrapperClassName = createCustomClassNames(className, `checkmark-wrapper`);
  const assetWrapperClassName = createCustomClassNames(className, `asset-wrapper`);
  const optionContentWrapperClassName = createCustomClassNames(className, `option-content-wrapper`);
  const shortcutWrapperClassName = createCustomClassNames(className, `shortcut-wrapper`);

  const payload = _objectSpread2({}, otherProps);

  delete payload.getActiveOption;
  delete payload.getHighlightIndex;
  delete payload.getOptionsInfo;
  delete payload.getPreviousEvent;
  delete payload.setActiveOption;
  delete payload.setHighlightIndex;
  const ariaPayload = role === `option` ? {
    "aria-selected": selected
  } : {};
  return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, _ref => {
    let {
      resolvedRoles,
      metadata
    } = _ref;
    const styles = stylesheet({
      disabled,
      highlighted,
      isPressed,
      role,
      selected,
      shortcut,
      stylesheet: customStylesheet,
      unselect
    }, resolvedRoles);
    const Checkmark = metadata.densityId === `medium-density` ? CheckmarkSUI : CheckmarkXsUI;
    return /*#__PURE__*/React.createElement("li", _extends({}, ariaPayload, payload, {
      className: cx([menuOptionClassName, css(styles.menuOption)]),
      disabled: disabled,
      id: id,
      ref: optionRef,
      role: role,
      selected: selected
    }), checkmark && role !== `presentation` ? /*#__PURE__*/React.createElement("div", {
      className: cx([checkmarkWrapperClassName, css(styles.checkmarkWrapper)])
    }, /*#__PURE__*/React.createElement(Checkmark, null)) : null, asset ? /*#__PURE__*/React.createElement("div", {
      className: cx([assetWrapperClassName, css(styles.assetWrapper)])
    }, asset) : null, /*#__PURE__*/React.createElement("div", {
      className: cx([optionContentWrapperClassName, css(styles.optionContentWrapper)])
    }, children, shortcut ? /*#__PURE__*/React.createElement("span", {
      className: cx([shortcutWrapperClassName, css(styles.shortcutWrapper)])
    }, shortcut) : null));
  });
};

OptionPresenter.displayName = "OptionPresenter";
OptionPresenter.propTypes = {
  asset: PropTypes.node,
  checkmark: PropTypes.bool,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  highlighted: PropTypes.bool,
  id: PropTypes.string,
  isPressed: PropTypes.bool,
  optionRef: PropTypes.func,
  role: PropTypes.oneOf(AVAILABLE_ROLES),
  selected: PropTypes.bool,
  shortcut: PropTypes.node,
  stylesheet: PropTypes.func,
  unselect: PropTypes.bool
};
OptionPresenter.__docgenInfo = {
  "description": "",
  "displayName": "OptionPresenter",
  "props": {
    "asset": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "checkmark": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "disabled": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "highlighted": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "id": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": ""
    },
    "isPressed": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "optionRef": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "role": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_ROLES"
      },
      "required": false,
      "description": ""
    },
    "selected": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "shortcut": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "unselect": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    }
  }
};

const _excluded$4 = ["asset", "children", "disabled", "id", "role", "shortcut", "stylesheet", "unselect"];

const Option = props => {
  const {
    asset,
    children,
    disabled,
    id,
    role,
    shortcut,
    stylesheet,
    unselect
  } = props,
        otherProps = _objectWithoutProperties(props, _excluded$4);

  const {
    getHighlightIndex,
    onFocus,
    onMouseDown,
    onMouseLeave,
    onMouseUp
  } = otherProps;
  return /*#__PURE__*/React.createElement(PressedBehavior, {
    onMouseDown: onMouseDown,
    onMouseUp: onMouseUp,
    onMouseLeave: onMouseLeave
  }, _ref => {
    let {
      isPressed,
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onPressedMouseLeave: handlePressedMouseLeave
    } = _ref;
    return /*#__PURE__*/React.createElement(OptionBehavior, _extends({}, otherProps, {
      disabled: disabled,
      id: id,
      onMouseLeave: handlePressedMouseLeave,
      role: role
    }), _ref2 => {
      let {
        getIndexFromId,
        handleClick,
        handleMouseEnter,
        handleMouseLeave,
        handleMouseOver,
        isActive
      } = _ref2;
      return /*#__PURE__*/React.createElement(OptionPresenter, _extends({}, otherProps, {
        asset: asset,
        disabled: disabled,
        highlighted: getHighlightIndex() === getIndexFromId(id) + 1,
        id: id,
        isPressed: isPressed,
        onClick: handleClick,
        onFocus: onFocus,
        onMouseDown: handleMouseDown,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onMouseOver: handleMouseOver,
        onMouseUp: handleMouseUp,
        role: role,
        selected: isActive(),
        shortcut: shortcut,
        stylesheet: stylesheet,
        unselect: unselect
      }), children);
    });
  });
};

Option.displayName = "Option";
Option.propTypes = {
  /**
   * Allows for an asset before the Option text
   * Can be from @hig/avatar, @hig/icons or
   * whatever image of yor choosing
   */
  asset: PropTypes.node,

  /**
   * Content of the Option
   */
  children: PropTypes.node.isRequired,

  /**
   * A callback ref that gets passed to the HTML 'li' element
   */
  optionRef: PropTypes.func,

  /**
   * Disables the Option
   */
  disabled: PropTypes.bool,

  /**
   * HTML unique id for the element
   */
  id: PropTypes.string.isRequired,

  /**
   * HTML attribute for accessibility
   */
  role: PropTypes.oneOf(AVAILABLE_ROLES),

  /**
   * Allows for a keyboard shortcut or any
   * content to the right of the Option content
   */
  shortcut: PropTypes.node,

  /**
   * Adds custom/overriding styles
   */
  stylesheet: PropTypes.func,

  /**
   * Allows unselecting an Option
   */
  unselect: PropTypes.bool
};
Option.defaultProps = {
  role: roles.OPTION
};
Option.__docgenInfo = {
  "description": "",
  "displayName": "Option",
  "props": {
    "asset": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Allows for an asset before the Option text\nCan be from @hig/avatar, @hig/icons or\nwhatever image of yor choosing"
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": "Content of the Option"
    },
    "optionRef": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "A callback ref that gets passed to the HTML 'li' element"
    },
    "disabled": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Disables the Option"
    },
    "id": {
      "type": {
        "name": "string"
      },
      "required": true,
      "description": "HTML unique id for the element"
    },
    "role": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "AVAILABLE_ROLES"
      },
      "required": false,
      "description": "HTML attribute for accessibility",
      "defaultValue": {
        "value": "roles.OPTION",
        "computed": true
      }
    },
    "shortcut": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": "Allows for a keyboard shortcut or any\ncontent to the right of the Option content"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Adds custom/overriding styles"
    },
    "unselect": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Allows unselecting an Option"
    }
  }
};

function createChildren(children, HIGComponent) {
  return Children.toArray(children).reduce((result, child, index) => {
    const {
      type,
      key,
      props = {
        index
      }
    } = child;

    if (type === HIGComponent) {
      result.push({
        key,
        props
      });
    }

    return result;
  }, []);
}

const _excluded$3 = ["checkmark", "children", "divider", "menuRef", "multiple", "selected", "stylesheet", "unselect"];

const MenuPresenter = props => {
  var _getOptionsInfo, _getOptionsInfo2;

  const {
    checkmark,
    children,
    divider,
    menuRef,
    multiple,
    selected,
    stylesheet: customStylesheet,
    unselect
  } = props,
        otherProps = _objectWithoutProperties(props, _excluded$3);

  const {
    id,
    className,
    getHighlightIndex,
    getOptionsInfo,
    role,
    tabIndex
  } = otherProps;
  const mounted = useRef();

  const getOptions = () => createChildren(props.children, Option);

  const renderOption = _ref => {
    let {
      key,
      props: propsOrigin
    } = _ref;
    const {
      getActiveOption,
      getPreviousEvent,
      onFocus,
      setActiveOption,
      setHighlightIndex
    } = props;

    const payload = _objectSpread2(_objectSpread2({}, propsOrigin), {}, {
      checkmark,
      getActiveOption,
      getHighlightIndex,
      getOptionsInfo,
      getPreviousEvent,
      multiple,
      key,
      onFocus,
      selected,
      setActiveOption,
      setHighlightIndex,
      unselect
    });

    return /*#__PURE__*/React.createElement(Option, payload);
  };

  const ariaPayload = role !== `group` ? _objectSpread2(_objectSpread2({}, getHighlightIndex() !== 0 && {
    "aria-activedescendant": (_getOptionsInfo = getOptionsInfo()) === null || _getOptionsInfo === void 0 ? void 0 : (_getOptionsInfo2 = _getOptionsInfo[getHighlightIndex() - 1]) === null || _getOptionsInfo2 === void 0 ? void 0 : _getOptionsInfo2.id
  }), multiple && {
    "aria-multiselectable": multiple
  }) : {};

  const payload = _objectSpread2({}, otherProps);

  delete payload.getActiveOption;
  delete payload.getHighlightIndex;
  delete payload.getOptionsInfo;
  delete payload.getPreviousEvent;
  delete payload.handleBlur;
  delete payload.handleFocus;
  delete payload.handleKeyDown;
  delete payload.handleMouseMove;
  delete payload.highlighted;
  delete payload.setActiveOption;
  delete payload.setHighlightIndex;
  delete payload.setOptionsInfo;
  delete payload.setPreviousEvent;
  delete payload.unselect;

  const renderOptions = () => getOptions().map(renderOption);

  useEffect(() => {
    if (!mounted.current) {
      const optionsInfo = [];

      if (role !== `group`) {
        React.Children.forEach(props.children, child => {
          if (child.type === Option) {
            optionsInfo.push(child.props);
          }
        });
        props.setOptionsInfo(optionsInfo);
      }

      mounted.current = true;
    } else if (props.role !== `group`) {
      const previousOptions = props.getOptionsInfo() || {};
      const optionsInfo = [];
      const currentIds = [];
      const prevIds = [];
      React.Children.forEach(props.children, child => {
        if (child.type === Option) {
          optionsInfo.push(child.props);
          currentIds.push(child.props.id);
        }
      });
      Object.keys(previousOptions).forEach(index => {
        prevIds.push(previousOptions[index].id);
      });

      if (JSON.stringify(currentIds) !== JSON.stringify(prevIds)) {
        props.setOptionsInfo(optionsInfo);
      }
    }
  });
  return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, _ref2 => {
    let {
      resolvedRoles
    } = _ref2;
    const styles = stylesheet({
      checkmark,
      divider,
      stylesheet: customStylesheet
    }, resolvedRoles);
    return /*#__PURE__*/React.createElement("ul", _extends({}, ariaPayload, payload, {
      className: cx([className, css(styles.menu)]),
      id: id,
      ref: menuRef,
      role: role || `listbox` // set to group if it is w/in a MenuGroup
      ,
      tabIndex: tabIndex || `0` // not focusable if w/in a MenuGroup

    }), renderOptions());
  });
};

MenuPresenter.displayName = "MenuPresenter";
MenuPresenter.propTypes = {
  checkmark: PropTypes.bool,
  children: PropTypes.node,
  divider: PropTypes.bool,
  getActiveOption: PropTypes.func,
  getOptionsInfo: PropTypes.func,
  getPreviousEvent: PropTypes.func,
  menuRef: PropTypes.func,
  multiple: PropTypes.bool,
  onFocus: PropTypes.func,
  role: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  selected: PropTypes.arrayOf(PropTypes.any),
  setActiveOption: PropTypes.func,
  setHighlightIndex: PropTypes.func,
  setOptionsInfo: PropTypes.func,
  stylesheet: PropTypes.func,
  unselect: PropTypes.bool
};

const _excluded$2 = ["checkmark", "children", "defaultSelected", "divider", "highlighted", "menuRef", "multiple", "onChange", "selected", "stylesheet", "unselect"];

const Menu = props => {
  const {
    checkmark,
    children,
    defaultSelected,
    divider,
    highlighted,
    menuRef,
    multiple,
    onChange = () => {},
    selected,
    stylesheet,
    unselect
  } = props,
        otherProps = _objectWithoutProperties(props, _excluded$2);

  const {
    onBlur,
    onFocus,
    onKeyDown
  } = otherProps;
  return /*#__PURE__*/React.createElement(FocusBehavior, {
    onBlur: onBlur,
    onFocus: onFocus
  }, _ref => {
    let {
      hasFocus,
      onBlur: handleBlur,
      onFocus: handleFocus
    } = _ref;
    return /*#__PURE__*/React.createElement(MenuBehavior, _extends({}, otherProps, {
      defaultSelected: defaultSelected,
      hasFocus: hasFocus,
      highlighted: highlighted,
      menuRef: menuRef,
      multiple: multiple,
      onBlur: handleBlur,
      onChange: onChange,
      onFocus: handleFocus,
      onKeyDown: onKeyDown,
      selected: selected,
      unselect: unselect
    }), _ref2 => {
      let {
        getActiveOption,
        getHighlightIndex,
        getOptionsInfo,
        getPreviousEvent,
        handleBlur: handleMenuBehaviorBlur,
        handleFocus: handleMenuBehaviorFocus,
        handleKeyDown,
        handleMouseMove,
        setActiveOption,
        setHighlightIndex,
        setMenuRef,
        setOptionsInfo,
        setPreviousEvent
      } = _ref2;
      return /*#__PURE__*/React.createElement(MenuPresenter, _extends({}, otherProps, {
        checkmark: checkmark,
        divider: divider,
        getActiveOption: getActiveOption,
        getHighlightIndex: getHighlightIndex,
        getOptionsInfo: getOptionsInfo,
        getPreviousEvent: getPreviousEvent,
        menuRef: setMenuRef,
        multiple: multiple,
        onBlur: handleMenuBehaviorBlur,
        onFocus: handleMenuBehaviorFocus,
        onKeyDown: handleKeyDown,
        onMouseMove: handleMouseMove,
        selected: selected,
        setActiveOption: setActiveOption,
        setHighlightIndex: setHighlightIndex,
        setOptionsInfo: setOptionsInfo,
        setPreviousEvent: setPreviousEvent,
        stylesheet: stylesheet
      }), children);
    });
  });
};

Menu.displayName = "Menu";
Menu.propTypes = {
  /**
   * Shows a checkmark selection indicator
   */
  checkmark: PropTypes.bool,

  /**
   * Accepts Option components
   */
  children: PropTypes.node.isRequired,

  /**
   * Default Selected Option(s)
   * Should be the HTML id of the Option
   */
  // eslint-disable-next-line react/forbid-prop-types
  defaultSelected: PropTypes.arrayOf(PropTypes.any),

  /**
   * Shows a divider at the bottom of the menu
   */
  divider: PropTypes.bool,

  /**
   * Semi-controlled way of having Options highlighted
   * Takes the index of the option you want highlighted
   */
  highlighted: PropTypes.number,

  /**
   * A callback ref that gets passed to the HTML
   * element with `role="listbox"`
   */
  menuRef: PropTypes.func,

  /**
   * Enables multiple selection
   */
  multiple: PropTypes.bool,

  /**
   * Called when an option is selected/unselected
   */
  onChange: PropTypes.func,

  /**
   * Triggers when a key is pressed
   * In addition to passing back the event you get the
   * following in an object:
   *  - currentHighlightedId: string
   */
  onKeyDown: PropTypes.func,

  /**
   * Controls the selected Option(s)
   * This will not work if this is the child
   * of a MenuGroup component
   */
  // eslint-disable-next-line react/forbid-prop-types
  selected: PropTypes.arrayOf(PropTypes.any),

  /**
   * Adds custom/overriding styles
   */
  stylesheet: PropTypes.func,

  /**
   * Shows a ability of checkmark to be unchecked if required
   */
  unselect: PropTypes.bool
};
Menu.defaultProps = {
  multiple: false,
  unselect: true
};
Menu.__docgenInfo = {
  "description": "",
  "displayName": "Menu",
  "props": {
    "checkmark": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Shows a checkmark selection indicator"
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": "Accepts Option components"
    },
    "defaultSelected": {
      "type": {
        "name": "arrayOf",
        "value": {
          "name": "any"
        }
      },
      "required": false,
      "description": "Default Selected Option(s)\nShould be the HTML id of the Option"
    },
    "divider": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Shows a divider at the bottom of the menu"
    },
    "highlighted": {
      "type": {
        "name": "number"
      },
      "required": false,
      "description": "Semi-controlled way of having Options highlighted\nTakes the index of the option you want highlighted"
    },
    "menuRef": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "A callback ref that gets passed to the HTML\nelement with `role=\"listbox\"`"
    },
    "multiple": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Enables multiple selection",
      "defaultValue": {
        "value": "false",
        "computed": false
      }
    },
    "onChange": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when an option is selected/unselected"
    },
    "onKeyDown": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Triggers when a key is pressed\nIn addition to passing back the event you get the\nfollowing in an object:\n - currentHighlightedId: string"
    },
    "selected": {
      "type": {
        "name": "arrayOf",
        "value": {
          "name": "any"
        }
      },
      "required": false,
      "description": "Controls the selected Option(s)\nThis will not work if this is the child\nof a MenuGroup component"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Adds custom/overriding styles"
    },
    "unselect": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Shows a ability of checkmark to be unchecked if required",
      "defaultValue": {
        "value": "true",
        "computed": false
      }
    }
  }
};

const _excluded$1 = ["getHighlightIndex", "getOptionsInfo", "menuRef", "multiple", "stylesheet", "unselect"];

const MenuGroupPresenter = props => {
  const mounted = useRef();

  const getMenus = () => createChildren(props.children, Menu);

  const renderMenu = _ref => {
    let {
      key,
      props: propsOrigin
    } = _ref;
    const {
      defaultSelected,
      getActiveOption,
      getHighlightIndex,
      getOptionsInfo,
      getPreviousEvent,
      highlighted,
      multiple,
      onBlur: handleBlur,
      onFocus: handleFocus,
      onKeyDown: handleKeyDown,
      onMouseMove: handleMouseMove,
      selected,
      setActiveOption,
      setHighlightIndex,
      setOptionsInfo,
      setPreviousEvent,
      unselect
    } = props;

    const payload = _objectSpread2(_objectSpread2({}, propsOrigin), {}, {
      defaultSelected,
      key,
      role: "group",
      tabIndex: "-1",
      getActiveOption,
      getHighlightIndex,
      getOptionsInfo,
      getPreviousEvent,
      handleBlur,
      handleFocus,
      handleKeyDown,
      handleMouseMove,
      highlighted,
      multiple,
      selected,
      setActiveOption,
      setHighlightIndex,
      setOptionsInfo,
      setPreviousEvent,
      unselect
    });

    return /*#__PURE__*/React.createElement(Menu, payload);
  };

  const renderMenus = () => getMenus().map(renderMenu);

  const {
    getHighlightIndex,
    getOptionsInfo,
    menuRef,
    multiple,
    stylesheet: customStylesheet,
    unselect
  } = props,
        otherProps = _objectWithoutProperties(props, _excluded$1);

  const {
    className
  } = otherProps;

  const ariaPayload = _objectSpread2(_objectSpread2({}, getHighlightIndex() !== 0 && {
    "aria-activedescendant": getOptionsInfo()[getHighlightIndex() - 1].id
  }), multiple && {
    "aria-multiselectable": multiple
  });

  const payload = _objectSpread2({}, otherProps);

  delete payload.defaultSelected;
  delete payload.getActiveOption;
  delete payload.getPreviousEvent;
  delete payload.setActiveOption;
  delete payload.setHighlightIndex;
  delete payload.setOptionsInfo;
  delete payload.setPreviousEvent;
  useEffect(() => {
    if (!mounted.current) {
      const menusInfo = {};
      const mergedOptions = [];
      React.Children.forEach(props.children, (child, index) => {
        menusInfo[index] = child.props;
      });
      Object.keys(menusInfo).forEach(index => {
        if (menusInfo[index] === Menu) {
          if (Array.isArray(menusInfo[index].children)) {
            menusInfo[index].children.forEach(child => {
              mergedOptions.push(child.props);
            });
          } else {
            mergedOptions.push(menusInfo[index].children.props);
          }
        }
      });
      props.setOptionsInfo(mergedOptions);
      mounted.current = true;
    } else {
      // update options if they change
      const previousOptions = props.getOptionsInfo();
      const menusInfo = {};
      const mergedOptions = [];
      const currentIds = [];
      const prevIds = []; // get current menus

      React.Children.forEach(props.children, (child, index) => {
        if (child.type === Menu) {
          menusInfo[index] = child.props;
        }
      }); // get current options

      Object.keys(menusInfo).forEach(index => {
        if (Array.isArray(menusInfo[index].children)) {
          menusInfo[index].children.forEach(child => {
            mergedOptions.push(child.props);
            currentIds.push(child.props.id);
          });
        } else {
          mergedOptions.push(menusInfo[index].children.props);
          currentIds.push(menusInfo[index].children.props.id);
        }
      }); // get previous options

      Object.keys(previousOptions).forEach(index => {
        prevIds.push(previousOptions[index].id);
      }); // check for changes and update option info if there is a change

      if (JSON.stringify(currentIds) !== JSON.stringify(prevIds)) {
        props.setOptionsInfo(mergedOptions);
      }
    }
  });
  return /*#__PURE__*/React.createElement(ThemeContext.Consumer, null, _ref2 => {
    let {
      resolvedRoles
    } = _ref2;
    const styles = stylesheet({
      stylesheet: customStylesheet
    }, resolvedRoles);
    return /*#__PURE__*/React.createElement("div", _extends({}, payload, ariaPayload, {
      className: cx([className, css(styles.menuGroup)]),
      ref: menuRef,
      role: "listbox" // conditional or required
      ,
      tabIndex: "0" // conditional w/ MenuGroup

    }), renderMenus());
  });
};

MenuGroupPresenter.displayName = "MenuGroupPresenter";
MenuGroupPresenter.propTypes = {
  children: PropTypes.node,
  // eslint-disable-next-line react/forbid-prop-types
  defaultSelected: PropTypes.arrayOf(PropTypes.any),
  getActiveOption: PropTypes.func,
  getHighlightIndex: PropTypes.func,
  getOptionsInfo: PropTypes.func,
  getPreviousEvent: PropTypes.func,
  highlighted: PropTypes.number,
  menuRef: PropTypes.func,
  multiple: PropTypes.bool,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  onMouseMove: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  selected: PropTypes.arrayOf(PropTypes.any),
  setActiveOption: PropTypes.func,
  setHighlightIndex: PropTypes.func,
  setOptionsInfo: PropTypes.func,
  setPreviousEvent: PropTypes.func,
  stylesheet: PropTypes.func,
  unselect: PropTypes.bool
};
MenuGroupPresenter.__docgenInfo = {
  "description": "",
  "displayName": "MenuGroupPresenter",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "defaultSelected": {
      "type": {
        "name": "arrayOf",
        "value": {
          "name": "any"
        }
      },
      "required": false,
      "description": ""
    },
    "getActiveOption": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "getHighlightIndex": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "getOptionsInfo": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "getPreviousEvent": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "highlighted": {
      "type": {
        "name": "number"
      },
      "required": false,
      "description": ""
    },
    "menuRef": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "multiple": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "onBlur": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onFocus": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onKeyDown": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onMouseMove": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "selected": {
      "type": {
        "name": "arrayOf",
        "value": {
          "name": "any"
        }
      },
      "required": false,
      "description": ""
    },
    "setActiveOption": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "setHighlightIndex": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "setOptionsInfo": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "setPreviousEvent": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "unselect": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    }
  }
};

const _excluded = ["children", "defaultSelected", "highlighted", "menuRef", "multiple", "onChange", "selected", "unselect", "stylesheet"];

const MenuGroup = props => {
  const {
    children,
    defaultSelected,
    highlighted,
    menuRef,
    multiple,
    onChange,
    selected,
    unselect,
    stylesheet
  } = props,
        otherProps = _objectWithoutProperties(props, _excluded);

  return /*#__PURE__*/React.createElement(MenuBehavior, _extends({}, otherProps, {
    defaultSelected: defaultSelected,
    highlighted: highlighted,
    menuRef: menuRef,
    multiple: multiple,
    onChange: onChange,
    selected: selected,
    unselect: unselect
  }), _ref => {
    let {
      getActiveOption,
      getHighlightIndex,
      getOptionsInfo,
      getPreviousEvent,
      handleBlur,
      handleFocus,
      handleKeyDown,
      handleMouseMove,
      setActiveOption,
      setHighlightIndex,
      setMenuRef,
      setOptionsInfo,
      setPreviousEvent
    } = _ref;
    return /*#__PURE__*/React.createElement(MenuGroupPresenter, _extends({}, otherProps, {
      defaultSelected: defaultSelected,
      getActiveOption: getActiveOption,
      getHighlightIndex: getHighlightIndex,
      getOptionsInfo: getOptionsInfo,
      getPreviousEvent: getPreviousEvent,
      menuRef: setMenuRef,
      multiple: multiple,
      onBlur: handleBlur,
      onFocus: handleFocus,
      onKeyDown: handleKeyDown,
      onMouseMove: handleMouseMove,
      selected: selected,
      setActiveOption: setActiveOption,
      setHighlightIndex: setHighlightIndex,
      setOptionsInfo: setOptionsInfo,
      setPreviousEvent: setPreviousEvent,
      stylesheet: stylesheet
    }), children);
  });
};

MenuGroup.displayName = "MenuGroup";
MenuGroup.propTypes = {
  /**
   * Accepts Menu components
   */
  children: PropTypes.node.isRequired,

  /**
   * Default Selected Option(s)
   * Should be the HTML id of the Option
   * This will take precedent over the Menu prop
   * of the same name
   */
  // eslint-disable-next-line react/forbid-prop-types
  defaultSelected: PropTypes.arrayOf(PropTypes.any),

  /**
   * Semi-controlled way of having Options highlighted
   * Takes the index of the option you want highlighted
   * This will take precedent over the Menu prop of the
   * same name
   */
  highlighted: PropTypes.number,

  /**
   * A callback ref that gets passed to the HTML
   * element with `role="listbox"`
   */
  menuRef: PropTypes.func,

  /**
   * Enables multiple selection
   * This will take precedent over the Menu prop
   * of the same name
   */
  multiple: PropTypes.bool,

  /**
   * Called when an option is selected/unselected
   * This will take precedent over the Menu prop of the
   * same name
   */
  onChange: PropTypes.func,

  /**
   * Controls the selected Option(s)
   * This will take precedent over the Menu prop of the
   * same name
   */
  // eslint-disable-next-line react/forbid-prop-types
  selected: PropTypes.arrayOf(PropTypes.any),

  /**
   * Adds custom/overriding styles
   * This will take precedent over the Menu prop of the
   * same name
   */
  stylesheet: PropTypes.func,

  /**
   * Shows a ability of checkmark to be unchecked if required
   */
  unselect: PropTypes.bool
};
MenuGroup.defaultProps = {
  multiple: false,
  unselect: true
};
MenuGroup.__docgenInfo = {
  "description": "",
  "displayName": "MenuGroup",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": "Accepts Menu components"
    },
    "defaultSelected": {
      "type": {
        "name": "arrayOf",
        "value": {
          "name": "any"
        }
      },
      "required": false,
      "description": "Default Selected Option(s)\nShould be the HTML id of the Option\nThis will take precedent over the Menu prop\nof the same name"
    },
    "highlighted": {
      "type": {
        "name": "number"
      },
      "required": false,
      "description": "Semi-controlled way of having Options highlighted\nTakes the index of the option you want highlighted\nThis will take precedent over the Menu prop of the\nsame name"
    },
    "menuRef": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "A callback ref that gets passed to the HTML\nelement with `role=\"listbox\"`"
    },
    "multiple": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Enables multiple selection\nThis will take precedent over the Menu prop\nof the same name",
      "defaultValue": {
        "value": "false",
        "computed": false
      }
    },
    "onChange": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Called when an option is selected/unselected\nThis will take precedent over the Menu prop of the\nsame name"
    },
    "selected": {
      "type": {
        "name": "arrayOf",
        "value": {
          "name": "any"
        }
      },
      "required": false,
      "description": "Controls the selected Option(s)\nThis will take precedent over the Menu prop of the\nsame name"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Adds custom/overriding styles\nThis will take precedent over the Menu prop of the\nsame name"
    },
    "unselect": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Shows a ability of checkmark to be unchecked if required",
      "defaultValue": {
        "value": "true",
        "computed": false
      }
    }
  }
};

export { AVAILABLE_ROLES, MenuGroup, Option, Menu as default, roles };
